/** The default bounded `created` window every /delivery-logs entry point
 * seeds (the page itself on first visit, and deep links from other grids).
 * It is load-bearing for performance: the table is daily-partitioned on
 * created_ts (UTC days) and unindexed lookups rely on the window for
 * partition pruning.
 *
 * Bounds are naive `yyyy-MM-dd HH:mm` strings the backend reads as UTC —
 * 00:00 on the first day to 23:59 on the last, aligned to UTC calendar days
 * so the window matches whole partitions.
 */
import { subDays } from 'date-fns';

import type { FilterNode } from '@/components/QuerySearch';

/** How far back the seeded `created` window reaches. */
export const DEFAULT_WINDOW_DAYS = 7;

/** The UTC calendar day of `d` as `yyyy-MM-dd` (toISOString is always UTC). */
function utcDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** `created` within the last `days` UTC days, as an editable range chip. */
export function createdWindowNode(days: number = DEFAULT_WINDOW_DAYS): FilterNode {
  const today = new Date();
  return {
    kind: 'cond',
    namespace: 'created',
    operator: 'range',
    value: [`${utcDay(subDays(today, days))} 00:00`, `${utcDay(today)} 23:59`],
  };
}

/** Whether the tree constrains `created` somewhere (the FE mirror of the
 * backend's `ast_has_time_bound`). Negated created leaves don't count — they
 * don't prune. Used by grids whose backend REQUIRES a time bound
 * (/api-requests) to gate the query and prompt for a window instead of
 * bouncing off the server's 400. */
export function hasCreatedTimeBound(nodes: FilterNode[]): boolean {
  for (const n of nodes) {
    if (n.not) continue;
    if (n.kind === 'cond' && n.namespace === 'created') return true;
    if (n.kind === 'group' && hasCreatedTimeBound(n.children)) return true;
  }
  return false;
}
